/**
 * dataTransformers.js - Transformadores y procesadores de datos
 * Funciones para normalizar, filtrar y transformar datos entre API y UI
 * IMPORTANTE: Todas las funciones deben respetar INMUTABILIDAD
 */

/**
 * Calcula los días para vencer de un registro
 * @param {object} record - Registro con fecha de expiración
 * @returns {number|null} Días para vencer o null si no aplica
 */
export const calculateDaysToExpire = (record) => {
  if (!record.fechaExpiracion || record.estado !== 'activo') return null;
  
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalizar a medianoche
    
    // Manejar diferentes formatos de fecha
    let expireDate;
    if (record.fechaExpiracion.toDate) {
      // Firestore Timestamp
      expireDate = record.fechaExpiracion.toDate();
    } else if (record.fechaExpiracion._seconds) {
      // Firestore Timestamp serializado
      expireDate = new Date(record.fechaExpiracion._seconds * 1000);
    } else {
      // String o Date normal
      expireDate = new Date(record.fechaExpiracion);
    }
    
    if (isNaN(expireDate)) return null;
    
    expireDate.setHours(0, 0, 0, 0);
    const diffTime = expireDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculando días para vencer:', error);
    return null;
  }
};

/**
 * Transforma un registro de la API al formato esperado por la UI
 * FUNCIÓN PURA - No muta el registro original
 * @param {object} rawRecord - Registro crudo de la API
 * @returns {object} Registro transformado (NUEVO OBJETO)
 */
export const transformRecord = (rawRecord) => {
  if (!rawRecord) return null;
  
  const diasParaVencer = calculateDaysToExpire(rawRecord);
  
  // CORRECCIÓN CRÍTICA: Crear un objeto completamente nuevo
  // Primero copiamos TODAS las propiedades originales, luego las transformadas
  return {
    // IMPORTANTE: Spread operator para copiar todas las propiedades originales
    ...rawRecord,
    
    // Ahora sobrescribimos/agregamos las propiedades transformadas
    // IDs
    id: rawRecord.id || rawRecord._id || '',
    // Mantenemos AMBOS nombres para compatibilidad
    ownerId: rawRecord.ownerId || rawRecord.ownerId || null,
    userId: rawRecord.ownerId || rawRecord.ownerId || null, // Alias para compatibilidad
    
    // Información básica
    nombre: rawRecord.nombre || rawRecord.nombreObjeto || 'Sin nombre',
    tipo: rawRecord.tipo || 'objeto',
    folio: rawRecord.folio || '',
    codigoAcceso: rawRecord.codigoAcceso || '',
    
    // Estados
    estado: rawRecord.estado || 'inactivo',
    estadoImpresion: rawRecord.estadoImpresion || 'pendiente',
    extraviado: Boolean(rawRecord.extraviado),
    
    // Plan y vigencia
    plan: rawRecord.plan || 'light',
    duracionPlanMeses: rawRecord.duracionPlanMeses || 12,
    fechaExpiracion: rawRecord.fechaExpiracion,
    diasParaVencer: diasParaVencer,
    expira: formatExpirationDate(rawRecord, diasParaVencer),
    
    // Pipeline visual (objeto nuevo)
    pipeline: buildPipeline(rawRecord),
    
    // Datos adicionales
    descripcion: rawRecord.descripcion || '',
    ubicacionPerdida: rawRecord.ubicacionPerdida || '',
    recompensa: rawRecord.recompensa || '',
    
    // Timestamps
    createdAt: rawRecord.createdAt,
    fechaActivacion: rawRecord.fechaActivacion,
    updatedAt: rawRecord.updatedAt,
    
    // Datos específicos por tipo (objeto nuevo)
    ...extractTypeSpecificData(rawRecord)
  };
};

/**
 * Transforma múltiples registros - FUNCIÓN PURA
 * @param {array} records - Array de registros crudos
 * @returns {array} NUEVO array con registros transformados
 */
export const transformRecords = (records) => {
  if (!Array.isArray(records)) return [];
  
  // Usar map para crear un NUEVO array con NUEVOS objetos
  return records.map(record => transformRecord(record));
};

/**
 * Construye el objeto pipeline basado en los estados
 * @param {object} record - Registro
 * @returns {object} Estado del pipeline (NUEVO OBJETO)
 */
export const buildPipeline = (record) => {
  // Siempre retorna un objeto nuevo
  return {
    inactivo: true, // Siempre empieza como inactivo
    impreso: ['impreso', 'entregado'].includes(record.estadoImpresion),
    entregado: record.estadoImpresion === 'entregado',
    activado: record.estado === 'activo'
  };
};

/**
 * Formatea la fecha de expiración para mostrar
 * @param {object} record - Registro
 * @param {number} diasParaVencer - Días calculados para vencer
 * @returns {string} Fecha formateada
 */
export const formatExpirationDate = (record, diasParaVencer) => {
  if (!record.fechaExpiracion) {
    if (record.estado === 'activo') return 'Sin fecha de expiración';
    return 'Sin activar';
  }
  
  try {
    const date = record.fechaExpiracion.toDate ? 
      record.fechaExpiracion.toDate() : 
      new Date(record.fechaExpiracion);
    
    if (isNaN(date)) return 'Fecha inválida';
    
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Error en fecha';
  }
};

/**
 * Extrae datos específicos según el tipo de registro
 * FUNCIÓN PURA - Retorna objeto nuevo
 * @param {object} record - Registro
 * @returns {object} Datos específicos del tipo (NUEVO OBJETO)
 */
export const extractTypeSpecificData = (record) => {
  // Siempre crear un objeto nuevo
  const typeData = {};
  
  switch (record.tipo) {
    case 'mascota':
      typeData.especie = record.especie || '';
      typeData.raza = record.raza || '';
      typeData.color = record.color || '';
      typeData.edad = record.edad || '';
      typeData.chip = record.chip || '';
      typeData.esterilizado = Boolean(record.esterilizado);
      typeData.alergias = record.alergias || '';
      typeData.veterinarios = record.veterinarios ? [...record.veterinarios] : []; // Copiar array
      break;
      
    case 'persona':
      typeData.personaDeContacto = record.personaDeContacto || '';
      typeData.telefonoDelContacto = record.telefonoDelContacto || '';
      typeData.parentescoDelContacto = record.parentescoDelContacto || '';
      typeData.tipoSangrePropietario = record.tipoSangrePropietario || '';
      break;
      
    case 'llavero':
      typeData.coleccion = record.coleccion || '';
      typeData.numeroPieza = record.numeroPieza || '';
      typeData.totalPiezas = record.totalPiezas || '';
      break;
  }
  
  return typeData;
};

/**
 * Transforma un usuario de la API al formato de UI
 * FUNCIÓN PURA - No muta el usuario original
 * @param {object} rawUser - Usuario crudo
 * @returns {object} Usuario transformado (NUEVO OBJETO)
 */
export const transformUser = (rawUser) => {
  if (!rawUser) return null;
  
  // Crear objeto completamente nuevo
  return {
    ...rawUser, // Copiar todas las propiedades originales primero
    id: rawUser.id || rawUser._id || rawUser.uid || '',
    nombre: rawUser.nombre || rawUser.displayName || 'Usuario sin nombre',
    email: rawUser.email || rawUser.correo || '',
    telefono: rawUser.telefono || rawUser.phone || '',
    clienteDesde: rawUser.clienteDesde || rawUser.createdAt || rawUser.fechaRegistro,
    totalObjetos: rawUser.totalObjetos || 0,
    notas: rawUser.notas || '',
    rol: rawUser.rol || 'usuario',
    activo: rawUser.activo !== false, // Por defecto true
    photoURL: rawUser.photoURL || rawUser.foto || ''
  };
};

export const getFilteredRecords = (records, filterType = 'all', quickFilter = '') => {
  if (!Array.isArray(records)) return [];
  
  // IMPORTANTE: Crear copia del array
  let filtered = [...records];
  
  // Aplicar filtro rápido de métricas
  if (quickFilter) {
    switch(quickFilter) {
      case 'inactivos': // CORREGIDO: era 'pendientes'
        filtered = filtered.filter(r => r.estado === 'inactivo');
        break;
      case 'pendientePorLigar': // NUEVO
        filtered = filtered.filter(r => r.estado === 'pendientePorLigar');
        break;
      case 'impresos':
        filtered = filtered.filter(r => r.estadoImpresion === 'impreso');
        break;
      case 'entregados':
        filtered = filtered.filter(r => r.estadoImpresion === 'entregado');
        break;
      case 'activos': // CORREGIDO: usar estado en lugar de pipeline
        filtered = filtered.filter(r => r.estado === 'activo');
        break;
      case 'perdidos': // CORREGIDO: usar extraviado
        filtered = filtered.filter(r => r.extraviado === true);
        break;
      case 'porVencer':
        filtered = filtered.filter(r => 
          r.diasParaVencer !== null && r.diasParaVencer >= 0 && r.diasParaVencer <= 30
        );
        break;
      case 'vencidos':
        filtered = filtered.filter(r => 
          r.diasParaVencer !== null && r.diasParaVencer < 0
        );
        break;
    }
  }
  
  // Aplicar filtro de tipo (resto del código sin cambios)
  if (filterType && filterType !== 'all') {
    switch(filterType) {
      case 'proximos-vencer':
        filtered = filtered.filter(r => 
          r.diasParaVencer !== null && r.diasParaVencer >= 0 && r.diasParaVencer <= 30
        );
        break;
      case 'vencidos':
        filtered = filtered.filter(r => 
          r.diasParaVencer !== null && r.diasParaVencer < 0
        );
        break;
    }
  }
  
  return filtered;
};

/**
 * Agrupa registros por usuario
 * FUNCIÓN PURA - No muta los arrays originales
 * @param {array} records - Registros a agrupar
 * @param {array} users - Lista de usuarios
 * @returns {array} Registros agrupados (NUEVO ARRAY)
 */
export const getGroupedResults = (records, users = []) => {
  const grouped = {};
  const userMap = new Map(users.map(u => [u.id, u]));
  
  records.forEach(record => {
    // Usar ownerId o userId
    const userId = record.ownerId || record.userId || 'sin-usuario';
    
    if (!grouped[userId]) {
      const user = userMap.get(userId) || {
        id: userId,
        nombre: 'Usuario sin cuenta',
        email: 'No activado',
        telefono: '',
        clienteDesde: null,
        totalObjetos: 0
      };
      
      grouped[userId] = {
        user: transformUser(user),
        records: []
      };
    }
    
    grouped[userId].records.push(record);
  });
  
  // Ordenar grupos y retornar nuevo array
  return Object.values(grouped).sort((a, b) => {
    // Usuarios con cuenta primero
    if (a.user.email === 'No activado' && b.user.email !== 'No activado') return 1;
    if (a.user.email !== 'No activado' && b.user.email === 'No activado') return -1;
    
    // Por cantidad de registros
    return b.records.length - a.records.length;
  });
};

/**
 * Prepara datos para exportación
 * FUNCIÓN PURA - No muta los registros originales
 * @param {array} records - Registros a exportar
 * @param {string} baseURL - URL base para enlaces
 * @returns {array} Datos listos para exportar (NUEVO ARRAY)
 */
export const prepareExportData = (records, baseURL) => {
  // Map crea un nuevo array con objetos nuevos
  return records.map(record => ({
    folio: record.folio || '',
    nombre: record.nombre || '',
    tipo: record.tipo || '',
    estado: record.estado || '',
    plan: record.plan || '',
    fechaExpiracion: record.expira || '',
    diasParaVencer: record.diasParaVencer !== null ? record.diasParaVencer : '',
    extraviado: record.extraviado ? 'Sí' : 'No',
    codigo: record.id || '',
    enlaceActivacion: `${baseURL}/activar?activacion=${record.codigoAcceso}`,
    enlaceConsulta: `${baseURL}/consultar?registro=${record.id}`
  }));
};

/**
 * Calcula métricas a partir de una lista de registros
 * FUNCIÓN PURA - No muta los registros originales
 * @param {array} records - Registros
 * @returns {object} Métricas calculadas (NUEVO OBJETO)
 */
export const calculateMetrics = (records) => {
  if (!Array.isArray(records)) {
    return {
      pendientes: 0,
      impresos: 0,
      entregados: 0,
      activos: 0,
      perdidos: 0,
      porVencer: 0,
      vencidos: 0,
      total: 0
    };
  }
  
  // Transformar creando nuevos objetos
  const transformedRecords = records.map(r => transformRecord(r));
  
  return {
    pendientes: transformedRecords.filter(r => !r.pipeline.impreso).length,
    impresos: transformedRecords.filter(r => r.pipeline.impreso && !r.pipeline.entregado).length,
    entregados: transformedRecords.filter(r => r.pipeline.entregado && !r.pipeline.activado).length,
    activos: transformedRecords.filter(r => r.pipeline.activado).length,
    perdidos: transformedRecords.filter(r => r.extraviado).length,
    porVencer: transformedRecords.filter(r => 
      r.diasParaVencer !== null && r.diasParaVencer >= 0 && r.diasParaVencer <= 30
    ).length,
    vencidos: transformedRecords.filter(r => 
      r.diasParaVencer !== null && r.diasParaVencer < 0
    ).length,
    total: records.length
  };
};

/**
 * Obtiene el ícono según el tipo de registro
 * FUNCIÓN PURA
 * @param {string} tipo - Tipo de registro
 * @returns {string} Emoji del ícono
 */
export const getTypeIcon = (tipo) => {
  const icons = {
    'mascota': '🐕',
    'persona': '👤',
    'llavero': '🔑',
    'objeto': '📦',
    'default': '📦'
  };
  
  return icons[tipo?.toLowerCase()] || icons.default;
};

/**
 * Obtiene la clase de borde según el estado del registro
 * FUNCIÓN PURA
 * @param {object} record - Registro
 * @returns {string} Clase CSS para el borde
 */
export const getCardBorderColor = (record) => {
  if (record.extraviado) return 'border-danger border-2';
  if (record.diasParaVencer !== null && record.diasParaVencer < 0) return 'border-danger';
  if (record.diasParaVencer !== null && record.diasParaVencer <= 30) return 'border-warning';
  if (record.pipeline?.activado) return 'border-success';
  return '';
};

/**
 * Obtiene el badge de vencimiento
 * FUNCIÓN PURA
 * @param {object} record - Registro
 * @returns {object|null} Configuración del badge o null (NUEVO OBJETO)
 */
export const getExpirationBadge = (record) => {
  if (record.diasParaVencer === null) return null;
  
  if (record.diasParaVencer < 0) {
    return {
      text: `Vencido hace ${Math.abs(record.diasParaVencer)} días`,
      className: 'bg-danger'
    };
  } else if (record.diasParaVencer <= 7) {
    return {
      text: `Vence en ${record.diasParaVencer} días`,
      className: 'bg-danger'
    };
  } else if (record.diasParaVencer <= 30) {
    return {
      text: `Vence en ${record.diasParaVencer} días`,
      className: 'bg-warning'
    };
  }
  
  return null;
};

// Exportar todo
export default {
  calculateDaysToExpire,
  transformRecord,
  transformRecords, // NUEVA función agregada
  transformUser,
  buildPipeline,
  formatExpirationDate,
  extractTypeSpecificData,
  getFilteredRecords,
  getGroupedResults,
  prepareExportData,
  calculateMetrics,
  getTypeIcon,
  getCardBorderColor,
  getExpirationBadge
};